import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Building2, Check, CheckCircle2, Eye, EyeOff, Lock, Mail, Phone, Search, Shield, User } from "lucide-react";
import { useAuthStore } from "../stores/auth-store";
import { useThemeStore } from "../stores/themeStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { isCryptoAvailable } from "../lib/crypto";

const steps = ["Empresa", "Admin", "Plano", "Confirmar"];
const initialForm = {
  typeCustomer: "Física",
  name: "",
  email: "",
  phone: "",
  document: "",
  companyName: "",
  postalCode: "",
  number: "",
  addressLine: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  stateCode: "",
  password: "",
  confirmPassword: "",
  acceptTerms: false,
  acceptDataProcessing: false,
  acceptMarketing: false,
};

const onlyDigits = (value) => (value || "").replace(/\D/g, "");
const isFilled = (value) => String(value || "").trim().length > 0;
const fieldClass = "h-11 rounded-lg border-slate-300 bg-white";

function passwordChecks(password, confirmPassword) {
  return [
    { label: "8 caracteres", valid: password.length >= 8 },
    { label: "Letra maiúscula", valid: /[A-Z]/.test(password) },
    { label: "Letra minúscula", valid: /[a-z]/.test(password) },
    { label: "Número", valid: /[0-9]/.test(password) },
    { label: "Caractere especial", valid: /[^A-Za-z0-9]/.test(password) },
    { label: "Confirmação igual", valid: isFilled(confirmPassword) && password === confirmPassword },
  ];
}

function Stepper({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-3">
      {steps.map((label, index) => {
        const done = index < currentStep;
        const active = index === currentStep;
        return (
          <div key={label} className="flex items-center gap-3">
            <div className="flex flex-col items-center gap-2">
              <div className={[
                "grid h-10 w-10 place-items-center rounded-full border text-sm font-semibold",
                done ? "border-emerald-500 bg-emerald-500 text-white" : active ? "border-[#0f2f57] bg-[#0f2f57] text-white" : "border-slate-300 bg-slate-100 text-slate-500",
              ].join(" ")}>
                {done ? <Check className="h-5 w-5" /> : index + 1}
              </div>
              <span className={active ? "text-sm font-semibold text-[#0f2f57]" : "text-sm text-slate-500"}>{label}</span>
            </div>
            {index < steps.length - 1 && <div className={done ? "h-px w-12 bg-emerald-500" : "h-px w-12 bg-slate-300"} />}
          </div>
        );
      })}
    </div>
  );
}

function RequiredLabel({ htmlFor, children }) {
  return <Label htmlFor={htmlFor}>{children} <span className="text-red-600">*</span></Label>;
}

export default function RegisterPage() {
  const { register: registerUser, isLoading } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [plansState, setPlansState] = useState({ loading: true, error: "", service: null, plans: [] });
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("/api/public/service-plans")
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.detail || "Erro ao consultar planos");
        if (!body?.service?.code || !Array.isArray(body?.plans) || body.plans.length === 0) {
          throw new Error("service-plans não retornou serviço e planos válidos");
        }
        if (active) setPlansState({ loading: false, error: "", service: body.service, plans: body.plans });
      })
      .catch((error) => {
        if (active) setPlansState({ loading: false, error: error.message, service: null, plans: [] });
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedPlan = useMemo(
    () => plansState.plans.find((plan) => String(plan.servicePlanId) === String(selectedPlanId)),
    [plansState.plans, selectedPlanId]
  );
  const checks = passwordChecks(form.password, form.confirmPassword);
  const validDocument = form.typeCustomer === "Jurídica" ? onlyDigits(form.document).length >= 14 : onlyDigits(form.document).length >= 11;
  const validStep1 = isFilled(form.name) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && onlyDigits(form.phone).length >= 10 && validDocument && (form.typeCustomer === "Física" || isFilled(form.companyName)) && onlyDigits(form.postalCode).length === 8 && isFilled(form.number) && isFilled(form.addressLine) && isFilled(form.district) && isFilled(form.city) && form.stateCode.trim().length === 2;
  const validStep2 = checks.every((item) => item.valid);
  const validStep3 = Boolean(selectedPlan);
  const validStep4 = validStep1 && validStep2 && validStep3 && form.acceptTerms && form.acceptDataProcessing;

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const searchCep = async () => {
    const cleanCep = onlyDigits(form.postalCode);
    if (cleanCep.length !== 8) {
      toast.error("Informe um CEP com 8 dígitos");
      return;
    }

    setCepLoading(true);
    try {
      const response = await fetch(`/api/cep/${cleanCep}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "CEP não encontrado");
      setForm((current) => ({
        ...current,
        addressLine: data.addressLine || "",
        district: data.district || "",
        city: data.city || "",
        state: data.state || data.stateCode || "",
        stateCode: data.stateCode || "",
      }));
      toast.success("CEP encontrado");
    } catch (error) {
      toast.error(error.message || "Erro ao buscar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 0 && !validStep1) return toast.error("Preencha os dados obrigatórios do cliente e endereço");
    if (step === 1 && !validStep2) return toast.error("A senha precisa atender todos os requisitos");
    if (step === 2 && !validStep3) return toast.error("Selecione um plano retornado por service-plans");
    setStep((current) => Math.min(current + 1, 3));
  };

  const submit = async () => {
    if (!validStep4) return;
    if (!isCryptoAvailable()) {
      toast.error("Seu navegador não suporta criptografia. Use um navegador moderno.");
      return;
    }

    const result = await registerUser(form.name, form.email, form.password, {
      contract_document: onlyDigits(form.document),
      contract_phone: onlyDigits(form.phone),
      contract_postal_code: onlyDigits(form.postalCode),
      contract_number: form.number,
      contract_address_line: form.addressLine,
      contract_complement: form.complement || null,
      contract_district: form.district,
      contract_city: form.city,
      contract_state: form.state || form.stateCode,
      contract_state_code: form.stateCode.toUpperCase(),
      contract_type_customer: form.typeCustomer,
      contract_company_name: form.typeCustomer === "Jurídica" ? form.companyName : null,
      contract_service_plan_id: Number(selectedPlan.servicePlanId),
    });

    if (result.success) {
      toast.success("Conta criada com sucesso");
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 text-slate-900">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-sm font-medium text-[#0f2f57]">
            <Shield className="h-7 w-7" />
            KawaFinance
          </Link>
          <button onClick={toggleTheme} className="text-sm text-slate-600 hover:text-slate-900">
            {theme === "dark" ? "Modo Claro" : "Modo Escuro"}
          </button>
        </div>

        <main className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <Stepper currentStep={step} />

          <div className="mx-auto mt-8 max-w-5xl">
            {step === 0 && (
              <section className="space-y-8">
                <header className="flex items-start gap-4">
                  <div className="grid h-12 w-12 place-items-center rounded-lg bg-[#0f2f57] text-white">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-[#0f2f57]">Identificação do cliente</h1>
                    <p className="mt-1 text-sm text-slate-600">Dados do tenant usados para montar o customer-intake.</p>
                  </div>
                </header>

                <div className="grid grid-cols-2 rounded-lg border border-slate-300 bg-slate-100 p-1">
                  {["Física", "Jurídica"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, typeCustomer: type }))}
                      className={form.typeCustomer === type ? "rounded-md bg-[#0f2f57] px-4 py-3 font-semibold text-white" : "rounded-md px-4 py-3 font-semibold text-slate-600"}
                    >
                      Pessoa {type}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-slate-800">Dados cadastrais</h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="name">{form.typeCustomer === "Jurídica" ? "Responsável" : "Nome completo"}</RequiredLabel>
                      <Input id="name" className={fieldClass} value={form.name} onChange={update("name")} />
                    </div>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="document">{form.typeCustomer === "Jurídica" ? "CNPJ" : "CPF"}</RequiredLabel>
                      <Input id="document" className={fieldClass} value={form.document} onChange={update("document")} />
                    </div>
                    {form.typeCustomer === "Jurídica" && (
                      <div className="space-y-2 md:col-span-2">
                        <RequiredLabel htmlFor="companyName">Razão social ou nome fantasia</RequiredLabel>
                        <Input id="companyName" className={fieldClass} value={form.companyName} onChange={update("companyName")} />
                      </div>
                    )}
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="email">E-mail</RequiredLabel>
                      <Input id="email" className={fieldClass} type="email" value={form.email} onChange={update("email")} />
                    </div>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="phone">Telefone</RequiredLabel>
                      <Input id="phone" className={fieldClass} value={form.phone} onChange={update("phone")} />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-slate-800">Endereço</h2>
                  <div className="grid gap-4 md:grid-cols-[185px_42px_minmax(0,1fr)_185px] md:items-end">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="postalCode">CEP</RequiredLabel>
                      <Input id="postalCode" className={fieldClass} value={form.postalCode} onChange={update("postalCode")} />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-11 w-11 rounded-lg border-slate-300 bg-white"
                      onClick={searchCep}
                      disabled={cepLoading}
                      aria-label={cepLoading ? "Buscando CEP" : "Buscar CEP"}
                      title={cepLoading ? "Buscando CEP" : "Buscar CEP"}
                    >
                      <Search className="h-5 w-5" />
                    </Button>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="addressLine">Endereço</RequiredLabel>
                      <Input id="addressLine" className={fieldClass} value={form.addressLine} onChange={update("addressLine")} />
                    </div>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="number">Número</RequiredLabel>
                      <Input id="number" className={fieldClass} value={form.number} onChange={update("number")} />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="complement">Complemento</Label>
                      <Input id="complement" className={fieldClass} placeholder="Apto, Sala, etc." value={form.complement} onChange={update("complement")} />
                    </div>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="district">Bairro</RequiredLabel>
                      <Input id="district" className={fieldClass} value={form.district} onChange={update("district")} />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px_175px]">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="city">Cidade</RequiredLabel>
                      <Input id="city" className={fieldClass} value={form.city} onChange={update("city")} />
                    </div>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="stateCode">UF</RequiredLabel>
                      <Input id="stateCode" className={fieldClass} maxLength={2} value={form.stateCode} onChange={update("stateCode")} />
                    </div>
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="country">País</RequiredLabel>
                      <Input id="country" className={fieldClass} value="Brasil" readOnly disabled />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {step === 1 && (
              <section className="mx-auto max-w-2xl space-y-6">
                <header className="text-center">
                  <h1 className="text-2xl font-semibold text-[#0f2f57]">Usuário administrador</h1>
                  <p className="mt-1 text-sm text-slate-600">Credenciais do primeiro usuário do tenant.</p>
                </header>
                {[["Nome", form.name, User], ["E-mail", form.email, Mail], ["Telefone", form.phone, Phone]].map(([label, value, Icon]) => (
                  <div key={label} className="relative rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <Icon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <div className="pl-8 text-sm"><span className="text-slate-500">{label}</span><div className="font-medium">{value}</div></div>
                  </div>
                ))}
                <div className="space-y-2">
                  <RequiredLabel htmlFor="password">Senha</RequiredLabel>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <Input id="password" type={showPassword ? "text" : "password"} className={`${fieldClass} pl-10 pr-10`} value={form.password} onChange={update("password")} />
                    <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                  </div>
                </div>
                <div className="space-y-2">
                  <RequiredLabel htmlFor="confirmPassword">Confirmar senha</RequiredLabel>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <Input id="confirmPassword" type={showConfirmPassword ? "text" : "password"} className={`${fieldClass} pl-10 pr-10`} value={form.confirmPassword} onChange={update("confirmPassword")} />
                    <button type="button" onClick={() => setShowConfirmPassword((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                  </div>
                </div>
                <div className="grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                  {checks.map((item) => <div key={item.label} className={item.valid ? "flex items-center gap-2 text-sm text-emerald-700" : "flex items-center gap-2 text-sm text-slate-500"}><CheckCircle2 className="h-4 w-4" />{item.label}</div>)}
                </div>
              </section>
            )}

            {step === 2 && (
              <section className="space-y-6">
                <header>
                  <h1 className="text-2xl font-semibold text-[#0f2f57]">Seleção de plano</h1>
                  <p className="mt-1 text-sm text-slate-600">Planos carregados do serviço {plansState.service?.code || "configurado"}.</p>
                </header>
                {plansState.loading && <div className="rounded-lg border border-slate-200 p-6 text-slate-600">Consultando service-plans...</div>}
                {plansState.error && <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700"><AlertCircle className="h-5 w-5" />{plansState.error}</div>}
                <div className="grid gap-4 md:grid-cols-3">
                  {plansState.plans.map((plan) => {
                    const selected = String(selectedPlanId) === String(plan.servicePlanId);
                    return (
                      <button key={plan.servicePlanId} type="button" onClick={() => setSelectedPlanId(plan.servicePlanId)} className={selected ? "rounded-lg border-2 border-[#0f2f57] bg-blue-50 p-5 text-left shadow-sm" : "rounded-lg border border-slate-200 bg-white p-5 text-left hover:border-[#0f2f57]"}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h2 className="text-lg font-semibold text-slate-900">{plan.name}</h2>
                            <p className="mt-1 min-h-10 text-sm text-slate-600">{plan.description || "Plano disponível para contratação."}</p>
                          </div>
                          {selected && <CheckCircle2 className="h-6 w-6 text-[#0f2f57]" />}
                        </div>
                        <div className="mt-5 text-2xl font-bold text-[#0f2f57]">{plan.priceLabel || `R$ ${plan.priceMonthly || 0}`}</div>
                        <ul className="mt-4 space-y-2 text-sm text-slate-600">
                          {(plan.features || []).slice(0, 4).map((feature, index) => <li key={`${plan.servicePlanId}-${index}`} className="flex gap-2"><Check className="h-4 w-4 text-emerald-600" />{String(feature)}</li>)}
                          {(!plan.features || plan.features.length === 0) && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600" />Recursos conforme contrato do serviço</li>}
                        </ul>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {step === 3 && (
              <section className="space-y-6">
                <header>
                  <h1 className="text-2xl font-semibold text-[#0f2f57]">Confirmação</h1>
                  <p className="mt-1 text-sm text-slate-600">Revise o payload antes de finalizar o customer-intake.</p>
                </header>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 p-4"><h2 className="font-semibold">Cliente</h2><p className="mt-2 text-sm text-slate-600">{form.typeCustomer} · {form.name}</p><p className="text-sm text-slate-600">{form.email} · {form.phone}</p></div>
                  <div className="rounded-lg border border-slate-200 p-4"><h2 className="font-semibold">Endereço</h2><p className="mt-2 text-sm text-slate-600">{form.addressLine}, {form.number}</p><p className="text-sm text-slate-600">{form.city}/{form.stateCode}</p></div>
                  <div className="rounded-lg border border-slate-200 p-4"><h2 className="font-semibold">Produto e plano</h2><p className="mt-2 text-sm text-slate-600">{plansState.service?.code}</p><p className="text-sm text-slate-600">{selectedPlan?.name} · ID {selectedPlan?.servicePlanId}</p></div>
                  <div className="rounded-lg border border-slate-200 p-4"><h2 className="font-semibold">Onboarding</h2><p className="mt-2 text-sm text-slate-600">Origin configurado no backend</p><p className="text-sm text-slate-600">ExternalId gerado pelo tenant</p></div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">O documento é enviado sem máscara no payload final e deve permanecer mascarado apenas em logs e diagnósticos.</div>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 text-sm"><Checkbox checked={form.acceptTerms} onCheckedChange={(checked) => setForm((current) => ({ ...current, acceptTerms: checked === true }))} />Aceito os termos obrigatórios de contratação.</label>
                  <label className="flex items-start gap-3 text-sm"><Checkbox checked={form.acceptDataProcessing} onCheckedChange={(checked) => setForm((current) => ({ ...current, acceptDataProcessing: checked === true }))} />Confirmo o envio dos dados.</label>
                  <label className="flex items-start gap-3 text-sm"><Checkbox checked={form.acceptMarketing} onCheckedChange={(checked) => setForm((current) => ({ ...current, acceptMarketing: checked === true }))} />Aceito receber comunicações opcionais.</label>
                </div>
              </section>
            )}

            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:justify-between">
              <Button type="button" variant="outline" onClick={() => setStep((current) => Math.max(current - 1, 0))} disabled={step === 0}>Voltar</Button>
              {step < 3 ? (
                <Button type="button" className="bg-[#0f2f57] hover:bg-[#123a6b]" onClick={nextStep} disabled={(step === 2 && (!validStep3 || plansState.loading))}>Próximo</Button>
              ) : (
                <Button type="button" className="bg-[#0f2f57] hover:bg-[#123a6b]" onClick={submit} disabled={!validStep4 || isLoading}>{isLoading ? "Finalizando" : "Finalizar contratação"}</Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
